import { makeStyles } from '@material-ui/core/styles';
import { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { color } from '../../../app/theme';
import IconEdit20 from '../../../assets/images/Icons/IconEdit20';
import defaultImageUrl from '../../../assets/images/svg/img_avatar_org.svg';
import { HashtagUrlText } from '../../shared/HashtagText';
import ImageDisplay from '../../shared/ImageDisplay';

const useStyles = makeStyles(
  {
    root: {
      height: 'calc(100vh - 477px)',
      overflowY: 'auto',
    },
    outcomeBlock: {
      display: 'flex',
      marginBottom: 12,
    },
    incomeMessage: {
      textAlign: 'right',
      marginBottom: 12,
      '& > div': {
        borderRadius: '12px',
        background: color.$8,
        padding: '8px 12px',
        fontSize: '13px',
        fontStyle: 'normal',
        fontWeight: '400',
        lineHeight: 'normal',
        color: color.$13,
        display: 'inline-block',
        whiteSpace: 'pre-wrap',
        overflowWrap: 'anywhere',
        maxWidth: 600,
        textAlign: 'left',
      },
    },
    outcomeMessage: {
      borderRadius: '12px',
      background: color.$12,
      padding: '8px 12px',
      fontSize: '13px',
      fontStyle: 'normal',
      fontWeight: '400',
      lineHeight: 'normal',
      color: color.$7,
      display: 'inline-block',
      maxWidth: 600,
      marginBottom: 12,
      whiteSpace: 'pre-wrap',
      overflowWrap: 'anywhere',
      '& > span': {
        color: color.$1,
        padding: 0,
        borderRadius: 0,
        backgroundColor: 'unset',
      },
      '& > a': {
        color: color.$7,
        textDecoration: 'underline',
      },
    },
    add: {
      fontSize: '13px',
      fontStyle: 'normal',
      fontWeight: '400',
      lineHeight: 'normal',
      color: color.$2,
      display: 'flex',
      alignItems: 'center',
      cursor: 'pointer',
      '& > svg': {
        marginRight: 4,
      },
    },
  },
  {
    name: 'MessageContent',
  },
);

function Income({ message }) {
  const classes = useStyles();
  return (
    <div className={classes.incomeMessage}>
      <div>{message}</div>
    </div>
  );
}

function Outcome({
  message,
  index,
  onOpenCorrectionModal,
  isShowCorrectionModalButton,
}) {
  // continueMessage
  const classes = useStyles();
  const { t } = useTranslation();
  const { icon } = useSelector(store => store.organization);
  const { pathKey } = useSelector(store => store.aiBot);

  return (
    <div className={classes.outcomeBlock}>
      <ImageDisplay
        style={{ objectFit: 'cover', borderRadius: '50%', marginRight: 12 }}
        src={icon || defaultImageUrl}
        width={44}
        height={44}
      />

      <div className={classes.outcomeContent}>
        <div
          className={classes.outcomeMessage}
          dangerouslySetInnerHTML={{
            __html: HashtagUrlText(message),
          }}
        />
        {isShowCorrectionModalButton && (
          <div className={classes.add} onClick={onOpenCorrectionModal}>
            <IconEdit20
              className={classes.icon}
              id={`icon-edit-${index}`}
              color={color.$2}
              hoverColor={color.$2}
            />
            {t(`${pathKey}:correctionRecordsSidebar.add`)}
          </div>
        )}
      </div>
    </div>
  );
}

const MessageContent = ({
  data,
  messages = [],
  isActive,
  onOpenCorrectionModal,
}) => {
  const classes = useStyles();
  const contentRef = useRef();
  const { keyword, welcomeMessage, continueMessage } = data;

  const messagesContent = useMemo(() => {
    return messages.map((message, index) => {
      const result = [];

      if (message?.income)
        result.push(
          <Income key={`Income-${index}`} message={message?.income} />,
        );
      if (message?.outcome)
        result.push(
          <>
            <Outcome
              key={`Outcome-${index}`}
              index={index}
              message={message?.outcome}
              onOpenCorrectionModal={() =>
                onOpenCorrectionModal({
                  question: message?.income,
                  originalAnswer: message?.outcome,
                })
              }
              isShowCorrectionModalButton={!isActive}
            />
          </>,
        );

      return result;
    });
  }, [messages, continueMessage]);

  useEffect(() => {
    if (contentRef?.current) {
      contentRef?.current.scrollTo(0, contentRef?.current.scrollHeight);
    }
  }, [messages]);

  return (
    <div ref={contentRef} className={classes.root}>
      <Income message={keyword} />
      <Outcome message={welcomeMessage} isShowCorrectionModalButton={false} />
      {messagesContent}
    </div>
  );
};

export default MessageContent;
